import { Component, type ReactNode } from 'react';
import { AuthContext } from '../contexts/AuthContext';

/**
 * Mock auth provider for dev preview routes.
 * Provides a fake auth context so dev routes work without Firebase.
 * Only used by /dev/* routes — production routes use the real AuthProvider.
 */

const noop = () => {};
const noopAsync = async () => {};
const noopPromise = () => Promise.resolve();

const mockAuthValue = {
  currentUser: { uid: 'dev-coach', email: 'dev@doublebagel.com', displayName: 'Dev Coach', emailVerified: true } as any,
  userProfile: { uid: 'dev-coach', displayName: 'Dev Coach', currentMode: 'coach' as const } as any,
  loading: false,
  playerProfile: null,
  coachProfile: { displayName: 'Dev Coach', verificationStatus: 'verified' as const } as any,
  currentMode: 'coach' as const,
  activeProfile: { displayName: 'Dev Coach' } as any,
  hasCoachProfile: true,
  isCoachVerified: true,
  isCoachPending: false,
  canSwitchToCoach: true,
  emailVerified: true,
  isAdmin: false,
  isRoot: false,
  isStaff: false,
  hasAdminPanelAccess: false,
  signup: noopAsync as any,
  login: noopAsync as any,
  logout: noopPromise,
  loginWithGoogle: noopAsync as any,
  loginWithApple: noopAsync as any,
  hasGoogleProvider: false,
  hasAppleProvider: false,
  acquireAuthLock: noop,
  releaseAuthLock: noop,
  refreshUserProfile: noopPromise,
  loadProfileForUid: noopPromise,
  updateProfile: noopPromise,
  updatePlayerProfile: noopPromise,
  updateCoachProfile: noopPromise,
  switchMode: noopPromise,
  applyAsCoach: noopPromise,
  submitCoachVerification: noopPromise,
  completeCoachOnboarding: noopPromise,
  changePassword: noopPromise,
  verifyPassword: noopPromise,
  hasPasswordProvider: false,
  markEmailVerified: noop,
  handleLogout: noopPromise,
};

export function DevAuthProvider({ children }: { children: ReactNode }) {
  return (
    <AuthContext.Provider value={mockAuthValue}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Error boundary for dev routes.
 * Shows errors inline instead of crashing to a blank page.
 */
export class DevErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  state = { hasError: false, error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', fontFamily: 'monospace', color: '#ff4444', background: '#0F1A2E', minHeight: '100vh' }}>
          <h2 style={{ color: '#F1F5F9', marginBottom: '1rem' }}>Dev Preview Error</h2>
          <pre style={{ whiteSpace: 'pre-wrap', color: '#ccc', fontSize: '13px', lineHeight: 1.6 }}>
            {this.state.error?.message}
          </pre>
          <p style={{ color: '#8899AA', marginTop: '1rem', fontSize: '13px' }}>
            This error occurred in the dev preview route. It may be caused by Firebase being offline.
          </p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            style={{ marginTop: '1rem', padding: '8px 16px', cursor: 'pointer', background: '#1A6B8A', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px' }}
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
